/**
 * Khaime SDK Core
 * Main SDK class for programmatic usage
 */

import { askForCustomer } from '../components/checkout/customer-step';
import { pulse, startStorePulse } from '../utils/store-pulse';
import { initProblems, logPlainError } from '../utils/plain-errors';
import { ApiClient, getApiClient, resetApiClient } from '../utils/api-client';
import { getCache } from '../utils/cache';
import { CartManager } from '../utils/cart-manager';
import { ComponentRegistry } from '../components/base';
import { KhaimeStorefrontElement } from '../components/storefront/khaime-storefront';
import { KhaimeProductCardElement } from '../components/storefront/khaime-product-card';
import { KhaimePayButtonElement } from '../components/checkout/khaime-pay-button';
import { KhaimePricingElement } from '../components/pricing/khaime-pricing';
import { KhaimeCartPageElement } from '../components/cart/khaime-cart-page';
import type {
  SDKOptions,
  StorefrontOptions,
  CheckoutOptions,
  PayButtonOptions,
  PricingOptions,
  SDKCallbacks,
  Product,
  SDKConfig,
  ProductQueryOptions,
  CurrencyChoice,
} from '../types';
import { getContainer } from '../utils';

declare const __SDK_VERSION__: string;

/**
 * SDK Version
 */
export const VERSION = typeof __SDK_VERSION__ !== 'undefined' ? __SDK_VERSION__ : '2.0.0';

/**
 * Khaime SDK
 */
export class KhaimeSDK {
  private static instance: KhaimeSDK | null = null;

  private merchantId: string | number;
  private sandbox: boolean;
  private debug: boolean;
  private api: ApiClient;
  private callbacks: SDKCallbacks = {};

  /**
   * Initialize the SDK (singleton)
   */
  static init(options: SDKOptions): KhaimeSDK {
    if (this.instance) {
      console.warn('[Khaime SDK] Already initialized. Returning existing instance.');
      return this.instance;
    }

    this.instance = new KhaimeSDK(options);
    return this.instance;
  }

  /**
   * Get existing SDK instance
   */
  static getInstance(): KhaimeSDK | null {
    return this.instance;
  }

  /**
   * Reset SDK (for testing)
   */
  static reset(): void {
    this.instance = null;
    resetApiClient();
    getCache().clear();
  }

  /**
   * Confirm a payment using the Khaime Payment Element
   * This is a standalone method that doesn't require SDK initialization
   *
   * @param options.display - 'modal' (default) opens a popup modal, 'inline' renders in a container
   * @param options.container - Required for inline mode: element ID or HTMLElement to render into
   */
  static async confirmPayment(options: {
    secret: string;
    publishable_key: string;
    stripe_account_id?: string;
    amount?: number;
    currency?: string;
    return_url?: string;
    display?: 'modal' | 'inline';
    container?: string | HTMLElement;
    onSuccess?: (result: { paymentIntent: any }) => void;
    onError?: (error: Error) => void;
    onReady?: () => void;
  }): Promise<{ error?: { message: string }; paymentIntent?: any }> {
    const { secret, publishable_key, stripe_account_id, amount, currency, display = 'modal', container } = options;

    if (!secret || !publishable_key) {
      const err = new Error('secret and publishable_key are required');
      options.onError?.(err);
      return { error: { message: err.message } };
    }

    // Inline mode - render payment form directly into container
    if (display === 'inline') {
      if (!container) {
        const err = new Error('container is required for inline display mode');
        options.onError?.(err);
        return { error: { message: err.message } };
      }

      try {
        const { renderInlinePayment } = await import('../components/checkout/inline-payment');
        return renderInlinePayment({
          container,
          clientSecret: secret,
          publishableKey: publishable_key,
          stripeAccountId: stripe_account_id,
          amount: amount ? amount / 100 : undefined,
          currency: currency || 'USD',
          onSuccess: (result) => {
            options.onSuccess?.({ paymentIntent: result });
          },
          onError: (error) => {
            options.onError?.(error);
          },
          onReady: options.onReady,
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Payment failed');
        options.onError?.(error);
        return { error: { message: error.message } };
      }
    }

    // Modal mode (default) - open payment modal
    try {
      const { openCheckoutModal } = await import('../components/checkout/checkout-modal');

      return new Promise((resolve) => {
        openCheckoutModal({
          merchantId: 'payment',
          sessionToken: secret,
          paymentGateway: 'stripe',
          clientSecret: secret,
          publishableKey: publishable_key,
          stripeAccountId: stripe_account_id,
          amount: amount ? amount / 100 : undefined,
          currency: currency || 'USD',
          onSuccess: (result) => {
            options.onSuccess?.({ paymentIntent: result });
            resolve({ paymentIntent: result });
          },
          onError: (error) => {
            options.onError?.(error);
            resolve({ error: { message: error.message } });
          },
          onClose: () => {
            resolve({ error: { message: 'Payment cancelled' } });
          },
        });
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Payment failed');
      options.onError?.(error);
      return { error: { message: error.message } };
    }
  }

  private constructor(options: SDKOptions) {
    initProblems(options).forEach(logPlainError);
    this.merchantId = options.merchantId;
    this.sandbox = options.sandbox ?? false;
    this.debug = options.debug ?? false;

    // Initialize API client
    this.api = getApiClient({
      merchantId: options.merchantId,
      apiKey: options.apiKey,
      sandbox: options.sandbox,
      apiBase: options.apiBase,
      debug: options.debug,
    });

    // Register core components
    this.registerComponents();

    if (options.storePulse !== false) this.connectStorePulse();

    // Preload data during idle
    this.api.preload();

    this.log('Initialized', { merchantId: this.merchantId, sandbox: this.sandbox });
  }

  /**
   * Register Web Components
   */
  private registerComponents(): void {
    ComponentRegistry.define('khaime-storefront', KhaimeStorefrontElement);
    ComponentRegistry.define('khaime-product-card', KhaimeProductCardElement);
    ComponentRegistry.define('khaime-pay-button', KhaimePayButtonElement);
    ComponentRegistry.define('khaime-pricing', KhaimePricingElement);
  }

  /**
   * Set event callbacks
   */
  public on(callbacks: Partial<SDKCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Create a storefront widget
   */
  public async storefront(
    container: string | HTMLElement,
    options: Omit<StorefrontOptions, 'container'> = {}
  ): Promise<KhaimeStorefrontElement> {
    const target = getContainer(container);
    if (!target) {
      throw new Error(`Container not found: ${container}`);
    }

    const el = document.createElement('khaime-storefront') as KhaimeStorefrontElement;
    el.setAttribute('merchant-id', String(this.merchantId));

    if (this.sandbox) el.setAttribute('sandbox', '');
    if (this.debug) el.setAttribute('debug', '');

    // Apply options as attributes
    if (options.productIds) el.setAttribute('product-ids', options.productIds);
    if (options.category) el.setAttribute('category', options.category);
    if (options.categoryId !== undefined && options.categoryId !== '') {
      el.setAttribute('category-id', String(options.categoryId));
    }
    if (options.categorySlug) el.setAttribute('category-slug', options.categorySlug);
    if (options.type) el.setAttribute('type', options.type);
    if (options.sort) el.setAttribute('sort', options.sort);
    if (options.columns) el.setAttribute('columns', String(options.columns));
    if (options.limit) el.setAttribute('limit', String(options.limit));
    if (options.cartDisplay) el.setAttribute('cart-display', options.cartDisplay);
    if (options.cartUrl) el.setAttribute('cart-url', options.cartUrl);
    if (Array.isArray(options.enabledCurrencies) && options.enabledCurrencies.length) {
      el.setAttribute(
        'enabled-currencies',
        options.enabledCurrencies.map((c) => String(c).toUpperCase()).join(',')
      );
    }

    // Forward config overrides (theme, layout, effects, features) to the element.
    // These get merged on top of the API-fetched config during loadConfig().
    const { theme, layout, effects, features } = options;
    if (theme || layout || effects || features) {
      el.setConfigOverrides({
        ...(theme ? { theme } : {}),
        ...(layout ? { layout } : {}),
        ...(effects ? { effects } : {}),
        ...(features ? { features } : {}),
      } as any);
    }

    // Set up event forwarding
    this.setupEventForwarding(el);

    // Append to DOM (triggers connectedCallback → loadConfig → applyOverrides)
    target.appendChild(el);

    // Wait for component to be defined
    await ComponentRegistry.whenDefined('khaime-storefront');

    return el;
  }

  /**
   * Create a pay button
   */
  public async payButton(
    container: string | HTMLElement,
    options: Omit<PayButtonOptions, 'container'>
  ): Promise<KhaimePayButtonElement> {
    const target = getContainer(container);
    if (!target) {
      throw new Error(`Container not found: ${container}`);
    }

    const el = document.createElement('khaime-pay-button') as KhaimePayButtonElement;
    el.setAttribute('merchant-id', String(this.merchantId));
    el.setAttribute('product-id', String(options.productId));

    if (this.sandbox) el.setAttribute('sandbox', '');
    if (options.label) el.setAttribute('label', options.label);
    if (options.variant) el.setAttribute('variant', options.variant);

    // Set up event forwarding
    this.setupEventForwarding(el);

    target.appendChild(el);

    await ComponentRegistry.whenDefined('khaime-pay-button');

    return el;
  }

  /**
   * Create a payment form from a pre-existing session token.
   * Used by the WordPress [khaime_checkout] shortcode.
   *
   * Usage:
   *   khaime.createPayment({
   *     sessionToken: 'khs_xxx',
   *     container: '#checkout',
   *     onSuccess: (result) => { ... },
   *   });
   */
  public async createPayment(options: {
    sessionToken: string;
    container?: string | HTMLElement;
    theme?: 'light' | 'dark';
    onSuccess?: (result: {
      sessionToken: string;
      orderId: string;
      amount: number;
      currency: string;
      paymentMethod: 'stripe' | 'paystack';
    }) => void;
    onError?: (error: Error) => void;
    onReady?: () => void;
  }): Promise<void> {
    const { sessionToken, onSuccess, onError, onReady } = options;

    if (!sessionToken) {
      const err = new Error('sessionToken is required');
      onError?.(err);
      throw err;
    }

    try {
      // Fetch session details to get payment credentials
      const session = await this.api.getCheckoutSession(sessionToken);

      this.log('Session loaded', { gateway: session.payment_gateway, amount: session.payment.amount });
      onReady?.();

      // Open payment modal
      const { openCheckoutModal } = await import('../components/checkout/checkout-modal');

      openCheckoutModal({
        merchantId: this.merchantId,
        sessionToken,
        paymentGateway: session.payment_gateway,
        clientSecret: session.stripe?.client_secret,
        publishableKey: session.stripe?.publishable_key,
        accessCode: session.paystack?.access_code,
        amount: session.payment.amount / 100, // Convert cents to dollars for display
        currency: session.payment.currency,
        onSuccess: (result) => {
          this.callbacks.onPaymentSuccess?.({
            ...result,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          });
          onSuccess?.(result);
        },
        onError: (error) => {
          this.callbacks.onPaymentError?.({
            error: error.message,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          });
          onError?.(error);
        },
        onClose: () => {},
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to load payment session');
      this.log('createPayment error', err.message);
      onError?.(err);
    }
  }

  /**
   * Initialize checkout (lazy loads checkout component)
   */
  public async checkout(
    container: string | HTMLElement,
    options: Omit<CheckoutOptions, 'container'>
  ): Promise<void> {
    const target = getContainer(container);
    if (!target) {
      throw new Error(`Container not found: ${container}`);
    }

    // Lazy load checkout component
    const { openCheckoutModal } = await import('../components/checkout/checkout-modal');

    // Checkout needs the shopper's name and email; ask when the site did not pass them.
    let { customerEmail, customerName } = options;
    if (!customerEmail || !customerName) {
      const customer = await askForCustomer();
      if (!customer) return;
      customerEmail = customer.email;
      customerName = customer.name;
    }

    // Initialize session
    const session = await this.api.initCheckout({
      ...options,
      customerEmail,
      customerName,
      currency: this.readChosenCurrency() || undefined,
    });

    // Open checkout modal
    openCheckoutModal({
      merchantId: this.merchantId,
      sessionToken: session.sessionToken,
      onSuccess: (result) => {
        this.callbacks.onPaymentSuccess?.({
          ...result,
          merchantId: this.merchantId,
          timestamp: Date.now(),
        });
      },
      onError: (error) => {
        this.callbacks.onPaymentError?.({
          error: error.message,
          merchantId: this.merchantId,
          timestamp: Date.now(),
        });
      },
      onClose: () => {},
    });
  }

  /**
   * Create a pricing table
   */
  public async pricing(
    container: string | HTMLElement,
    options: Omit<PricingOptions, 'container'> = {}
  ): Promise<KhaimePricingElement> {
    const target = getContainer(container);
    if (!target) {
      throw new Error(`Container not found: ${container}`);
    }

    const el = document.createElement('khaime-pricing') as KhaimePricingElement;
    el.setAttribute('merchant-id', String(this.merchantId));

    if (this.sandbox) el.setAttribute('sandbox', '');
    if (this.debug) el.setAttribute('debug', '');

    // Plan IDs and configs from options
    if (options.plans?.length) {
      el.setAttribute('plan-ids', options.plans.map((p) => p.productId).join(','));
      el.setAttribute('data-plan-configs', JSON.stringify(options.plans));
    }

    // Free plan config
    if (options.freePlan) {
      el.setAttribute('data-free-plan', JSON.stringify(options.freePlan));
    }

    // Theme overrides
    if (options.theme?.mode) el.setAttribute('theme-mode', options.theme.mode);
    if (options.theme?.colors?.primary) el.setAttribute('primary-color', options.theme.colors.primary);
    if (options.layout?.columns) el.setAttribute('columns', String(options.layout.columns));
    if (options.layout?.cardStyle) el.setAttribute('card-style', options.layout.cardStyle);

    // Set up event forwarding
    this.setupEventForwarding(el);

    target.appendChild(el);

    await ComponentRegistry.whenDefined('khaime-pricing');

    return el;
  }

  /**
   * Get products directly from API.
   * Priced in the shopper's chosen currency (`setCurrency`) unless `currency`
   * is given; the cart then follows the currency the products came back in.
   */
  public async getProducts(options: Omit<ProductQueryOptions, 'merchantId'> = {}): Promise<Product[]> {
    const currency = options.currency || this.readChosenCurrency() || undefined;
    const response = await this.api.getProducts({ ...options, currency });
    this.cart().adoptCurrency(response.products[0]?.currency);
    return response.products;
  }

  /**
   * Get a single product, priced like `getProducts`.
   */
  public async getProduct(productId: number, currency?: string): Promise<Product> {
    const product = await this.api.getProduct(productId, currency || this.readChosenCurrency() || undefined);
    this.cart().adoptCurrency(product.currency);
    pulse('product_view', {
      product_id: product.id,
      product_title: product.title,
      product_price: product.formatted_price,
      currency: product.currency,
    });
    return product;
  }

  /**
   * Load the store's one code so Store Pulse, live visitors, popups and chat
   * work on this website, and report cart adds to it. Never blocks or breaks
   * the shop: a failure here only means fewer numbers in Store Pulse.
   */
  private connectStorePulse(): void {
    if (typeof window === 'undefined') return;
    this.api
      .bootstrap()
      .then(boot => {
        if (!startStorePulse(boot.storePulse)) return;
        this.cart().setAddListener((item, quantity) =>
          pulse('add_to_cart', {
            product_id: item.product_id,
            product_title: item.title,
            variant: item.variant_name,
            quantity,
            product_price: item.formatted_price,
            currency: item.currency,
          })
        );
      })
      .catch(() => undefined);
  }

  /**
   * The currencies a shopper can pick. Show a currency picker only when
   * `canSwitch` is true: a store that sells in one currency lists only that
   * one, and checkout would refuse any other.
   */
  public async getCurrencies(): Promise<CurrencyChoice> {
    const boot = await this.api.bootstrap();
    const options = (boot.currencies || []).map(c => ({ code: c.code, symbol: c.symbol, name: c.name }));
    const canSwitch = boot.multicurrencyEnabled === true && options.length > 1;
    const fallback = boot.shopperCurrency || boot.storeCurrency || boot.detectedCurrency || options[0]?.code || 'USD';
    const chosen = this.readChosenCurrency();
    const current = canSwitch && chosen && options.some(o => o.code === chosen) ? chosen : fallback;
    return { current, options, canSwitch };
  }

  /**
   * Switch the shopper's currency. Remembered for this visitor, reprices the
   * cart (resolving once that is done), and later `getProducts`/`getProduct` calls use it (call them again
   * to reprice what is on screen). Refuses a currency the store does not offer.
   */
  public async setCurrency(code: string): Promise<CurrencyChoice> {
    const wanted = String(code || '').trim().toUpperCase();
    const choice = await this.getCurrencies();
    if (!choice.canSwitch) {
      throw new Error('This store sells in one currency only.');
    }
    if (!choice.options.some(o => o.code === wanted)) {
      throw new Error(`This store does not offer ${wanted || 'that currency'}.`);
    }
    try {
      localStorage.setItem(this.currencyStorageKey(), wanted);
    } catch {
      // Private mode or blocked storage: the switch still holds for this page.
    }
    this.chosenCurrency = wanted;
    await this.cart().setCurrency(wanted);
    return { ...choice, current: wanted };
  }

  private chosenCurrency: string | null = null;

  private currencyStorageKey(): string {
    return `khaime:currency:${this.merchantId}`;
  }

  private readChosenCurrency(): string | null {
    if (this.chosenCurrency) return this.chosenCurrency;
    try {
      const saved = localStorage.getItem(this.currencyStorageKey());
      if (saved && /^[A-Z]{3}$/.test(saved)) this.chosenCurrency = saved;
    } catch {
      // No storage: nothing remembered.
    }
    return this.chosenCurrency;
  }

  /**
   * Get SDK configuration
   */
  public async getConfig(sdkType: string = 'storefront'): Promise<SDKConfig> {
    return this.api.getConfig(sdkType);
  }

  /**
   * Get CartManager instance for this merchant
   */
  public cart(): CartManager {
    return CartManager.getInstance(this.merchantId, this.api, this.debug);
  }

  /**
   * Open cart checkout modal
   * Initiates the checkout flow for items in the cart.
   */
  public async openCartCheckout(options: {
    currency?: string;
    onSuccess?: (result: { sessionToken: string; orderId?: string; amount: number; currency: string; paymentMethod?: 'stripe' | 'paystack' }) => void;
    onError?: (error: Error) => void;
    onClose?: () => void;
  } = {}): Promise<void> {
    const cartManager = this.cart();
    const items = cartManager.getItems();

    if (items.length === 0) {
      throw new Error('Cart is empty');
    }

    pulse('checkout_start', {
      cart_value: cartManager.getSubtotalMinor(),
      currency: cartManager.getCurrency(),
      item_count: cartManager.getItemCount(),
    });

    const { openCartCheckoutModal } = await import('../components/checkout/cart-checkout-modal');

    openCartCheckoutModal({
      api: this.api,
      cartManager,
      merchantId: this.merchantId,
      currency: options.currency || cartManager.getCurrency(),
      onSuccess: (result) => {
        this.callbacks.onPaymentSuccess?.({
          ...result,
          paymentMethod: result.paymentMethod || 'stripe',
          merchantId: this.merchantId,
          timestamp: Date.now(),
        });
        options.onSuccess?.(result);
      },
      onError: (error) => {
        this.callbacks.onPaymentError?.({
          error: error.message,
          merchantId: this.merchantId,
          timestamp: Date.now(),
        });
        options.onError?.(error);
      },
      onClose: () => {
        options.onClose?.();
      },
    });
  }

  /**
   * Invalidate cache
   */
  public invalidateCache(): void {
    this.api.invalidateCache();
  }

  /**
   * Set up event forwarding from components to callbacks
   */
  private setupEventForwarding(element: HTMLElement): void {
    element.addEventListener('khaime:product-click', (e) => {
      this.callbacks.onProductClick?.((e as CustomEvent).detail.product);
    });

    element.addEventListener('khaime:add-to-cart', (e) => {
      const detail = (e as CustomEvent).detail;
      this.callbacks.onAddToCart?.(detail.product, detail.quantity);
    });

    element.addEventListener('khaime:checkout-init', (e) => {
      this.callbacks.onCheckoutInit?.((e as CustomEvent).detail.sessionToken);
    });

    element.addEventListener('khaime:payment-success', (e) => {
      this.callbacks.onPaymentSuccess?.((e as CustomEvent).detail);
    });

    element.addEventListener('khaime:payment-error', (e) => {
      this.callbacks.onPaymentError?.((e as CustomEvent).detail);
    });

    element.addEventListener('khaime:currency-change', (e) => {
      this.callbacks.onCurrencyChange?.((e as CustomEvent).detail.newCurrency);
    });

    element.addEventListener('khaime:cart-updated', (e) => {
      const detail = (e as CustomEvent).detail;
      this.callbacks.onCartUpdated?.(detail.items, detail.action);
    });

    element.addEventListener('khaime:cart-open', () => {
      this.callbacks.onCartOpen?.();
    });

    element.addEventListener('khaime:cart-close', () => {
      this.callbacks.onCartClose?.();
    });

    element.addEventListener('khaime:product-detail-open', (e) => {
      this.callbacks.onProductDetailOpen?.((e as CustomEvent).detail.product);
    });

    element.addEventListener('khaime:product-detail-close', () => {
      this.callbacks.onProductDetailClose?.();
    });

    element.addEventListener('khaime:plan-selected', (e) => {
      this.callbacks.onPlanSelected?.((e as CustomEvent).detail);
    });
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[Khaime SDK]', ...args);
    }
  }

  // ─── Static convenience methods for WordPress plugin / script-tag usage ───
  // These auto-initialize the SDK and delegate to instance methods.
  // Allows: Khaime.storefront({ container, merchantId, ... })

  /**
   * Static: create a storefront from a flat config object
   */
  static async storefront(config: {
    container: string | HTMLElement;
    merchantId: number | string;
    sandbox?: boolean;
    debug?: boolean;
    apiBase?: string;
    productUrl?: string;
    [key: string]: unknown;
  }): Promise<KhaimeStorefrontElement> {
    const sdk = KhaimeSDK.init({
      merchantId: config.merchantId,
      sandbox: config.sandbox,
      debug: config.debug,
      apiBase: config.apiBase,
    });
    return sdk.storefront(config.container, config as any);
  }

  /**
   * Static: create a product detail page from a flat config object
   */
  static async productPage(config: {
    container: string | HTMLElement;
    merchantId: number | string;
    slug?: string;
    storeUrl?: string;
    sandbox?: boolean;
    debug?: boolean;
    apiBase?: string;
    [key: string]: unknown;
  }): Promise<void> {
    const sdk = KhaimeSDK.init({
      merchantId: config.merchantId,
      sandbox: config.sandbox,
      debug: config.debug,
      apiBase: config.apiBase,
    });

    const target = getContainer(config.container);
    if (!target) {
      throw new Error(`Container not found: ${config.container}`);
    }

    // Determine slug from config or URL path
    const slug = config.slug || KhaimeSDK.getSlugFromUrl();
    if (!slug) {
      throw new Error('No product slug provided or found in URL');
    }

    // Fetch products and find by slug
    const products = await sdk.getProducts();
    const product = products.find((p: any) => p.slug === slug);
    if (!product) {
      throw new Error(`Product not found: ${slug}`);
    }

    // Create storefront element in single-product mode
    const el = document.createElement('khaime-storefront') as KhaimeStorefrontElement;
    el.setAttribute('merchant-id', String(config.merchantId));
    el.setAttribute('product-ids', String(product.id));
    if (config.sandbox) el.setAttribute('sandbox', '');
    if (config.storeUrl) el.setAttribute('store-url', config.storeUrl);

    target.appendChild(el);
  }

  /**
   * Static: open checkout from a flat config object
   */
  static async checkout(config: {
    container: string | HTMLElement;
    merchantId: number | string;
    sandbox?: boolean;
    debug?: boolean;
    apiBase?: string;
    [key: string]: unknown;
  }): Promise<void> {
    const sdk = KhaimeSDK.init({
      merchantId: config.merchantId,
      sandbox: config.sandbox,
      debug: config.debug,
      apiBase: config.apiBase,
    });
    return sdk.checkout(config.container, config as any);
  }

  /**
   * Static: create a pay button from a flat config object
   */
  static async payButton(config: {
    container: string | HTMLElement;
    merchantId: number | string;
    productId: number | string;
    sandbox?: boolean;
    debug?: boolean;
    apiBase?: string;
    [key: string]: unknown;
  }): Promise<KhaimePayButtonElement> {
    const sdk = KhaimeSDK.init({
      merchantId: config.merchantId,
      sandbox: config.sandbox,
      debug: config.debug,
      apiBase: config.apiBase,
    });
    return sdk.payButton(config.container, config as any);
  }

  /**
   * Static: render a cart page from a flat config object.
   * `display: 'mini'` renders a compact sidebar variant that links to
   * `cartUrl` via its "View cart" CTA. Theme is inherited from the
   * merchant's bootstrap config.
   */
  static async cartPage(config: {
    container: string | HTMLElement;
    merchantId: number | string;
    display?: 'full' | 'mini';
    cartUrl?: string;
    storeUrl?: string;
    sandbox?: boolean;
    debug?: boolean;
    apiBase?: string;
    enabledCurrencies?: string[];
  }): Promise<KhaimeCartPageElement> {
    KhaimeSDK.init({
      merchantId: config.merchantId,
      sandbox: config.sandbox,
      debug: config.debug,
      apiBase: config.apiBase,
    });

    const target = getContainer(config.container);
    if (!target) {
      throw new Error(`Container not found: ${config.container}`);
    }

    const el = document.createElement('khaime-cart-page') as KhaimeCartPageElement;
    el.setAttribute('merchant-id', String(config.merchantId));
    if (config.sandbox) el.setAttribute('sandbox', '');
    if (config.debug) el.setAttribute('debug', '');
    if (config.display === 'mini') el.setAttribute('display', 'mini');
    if (config.cartUrl) el.setAttribute('cart-url', config.cartUrl);
    if (config.storeUrl) el.setAttribute('store-url', config.storeUrl);
    if (Array.isArray(config.enabledCurrencies) && config.enabledCurrencies.length) {
      el.setAttribute(
        'enabled-currencies',
        config.enabledCurrencies.map((c) => String(c).toUpperCase()).join(',')
      );
    }

    target.appendChild(el);
    return el;
  }

  /**
   * Static: create a pricing table from a flat config object
   */
  static async pricing(config: {
    container: string | HTMLElement;
    merchantId?: number | string;
    merchantSlug?: string;
    sandbox?: boolean;
    debug?: boolean;
    apiBase?: string;
    plans?: Array<{ productId: number; [key: string]: unknown }>;
    [key: string]: unknown;
  }): Promise<KhaimePricingElement> {
    const merchantId = config.merchantId ?? config.merchantSlug;
    if (!merchantId) {
      throw new Error('merchantId or merchantSlug is required');
    }

    const sdk = KhaimeSDK.init({
      merchantId,
      sandbox: config.sandbox,
      debug: config.debug,
      apiBase: config.apiBase,
    });
    return sdk.pricing(config.container, config as any);
  }

  /**
   * Extract product slug from current URL path
   * Matches patterns like /product/{slug} or /products/{slug}
   */
  private static getSlugFromUrl(): string | null {
    const path = window.location.pathname;
    const match = path.match(/\/products?\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
}

export default KhaimeSDK;
